/**
 * PlanLedger — SP-8 + B-4/B-5 density card (flag `today_density_v1`).
 *
 * Collapses the season strip + weekly grid into ONE paper card, mounted where
 * SeasonGlance did. Two rows:
 *
 *   Row A · THIS WEEK — 7 collapsed day-chips (46pt tap targets). B309: a tap
 *     on a chip (today's included) opens that day's details below the chips
 *     and never writes; done / not done is a button inside the details.
 *     "Details" expands the full current grid in place (reuses
 *     <WeekPlan>); the disclosure state persists per session. Omitted entirely
 *     when there's no weekly plan (the undrafted-week state — an empty week row
 *     would be filler).
 *
 *   Row B · SEASON — a fixed-fraction micro-track (one segment per macro week):
 *     drafted = solid + mode tint, not-drafted = dashed hollow (existing
 *     drafted/not-drafted vocabulary only — SP-7 locked, no new per-week
 *     status words); current week = yellow ring; ◆ = meet; past dimmed. A
 *     sub-line ("W2 of 7 · Base · State Meet in 3 wks") + "Draft ahead ›" when
 *     undrafted upcoming weeks exist. The track is NOT per-cell navigable — the
 *     row links to /v2/plan, which owns per-week navigation.
 *
 * Composes EXISTING inputs only: chips reuse `Day[]` + the WeekPlan `onToggle`
 * contract; the track derives from `MacroPlanWeek[]` + `draftedWeeks` +
 * `meetLabels` (the SeasonGlance inputs). No new nav; every link target already
 * exists.
 */
import { type CSSProperties, type ReactNode, useCallback, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Day, WeeklyPlan } from '../../fixtures/plan';
import type { MacroPlanWeek } from '../../hooks/useMacroPlan';
import { WeekPlan } from '../WeekPlan/WeekPlan';
import { DayDetails } from '../WeekPlan/DayDetails';
import { Icon } from '../icons/registry';
import { useIconSystem } from '../../hooks/useIconSystem';
import styles from './PlanLedger.module.css';

const PHASE_LABEL: Record<string, string> = {
  base: 'Base',
  build: 'Build',
  competition: 'Competition',
  taper: 'Taper',
  recovery: 'Recovery',
};

const SEG_MODE_CLASS: Record<string, string> = {
  base: styles.segBase,
  build: styles.segBuild,
  competition: styles.segCompetition,
  taper: styles.segTaper,
  recovery: styles.segRecovery,
};

/** A space the line may not break at (B354). */
const NBSP = ' ';

/** Disclosure state key — persists the expanded week row for the session. */
const DISCLOSURE_KEY = 'today_density_week_open';

function readDisclosure(): boolean {
  try {
    return globalThis.sessionStorage?.getItem(DISCLOSURE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeDisclosure(open: boolean): void {
  try {
    globalThis.sessionStorage?.setItem(DISCLOSURE_KEY, open ? '1' : '0');
  } catch {
    /* sessionStorage unavailable (private mode / SSR) — non-fatal. */
  }
}

export interface PlanLedgerProps {
  /** This week's plan — drives Row A. Null → Row A is omitted (undrafted
   *  week: no drafted weekly plan exists yet, so an empty grid would be
   *  filler). */
  weekPlan: WeeklyPlan | null;
  /** WeekPlan toggle contract — chips with an `id` open that day's details
   *  (today included); the details button is the only caller. Absent (demo) →
   *  chips are read-only. */
  onToggleDay?: (dayId: string, currentStatus: Day['completionStatus']) => Promise<unknown>;
  /** Macro-plan weeks (sorted ASC) — drives Row B's micro-track. */
  weeks: MacroPlanWeek[];
  /** Current week number (the "Now" cell + the sub-line anchor). */
  currentWeek: number | null;
  /** Season display name — Row B kicker ("SEASON · SPRING 2026 XC"). */
  seasonName?: string | null;
  /** Week numbers with a drafted weekly plan. Undefined → treat all as
   *  drafted (no dashed cells) — the demo showcase passes an explicit set. */
  draftedWeeks?: Set<number>;
  /** Meet labels per week_number — surface as ◆ flags + the sub-line
   *  countdown to the culminating (last upcoming) meet. */
  meetLabels?: Record<number, string>;
  /** Count of undrafted upcoming weeks — gates "Draft ahead ›". */
  undraftedUpcomingCount: number;
  /** "Open week ›" target (→ /v2/plan/week/N). */
  weekHref: string;
  /** "Open plan ›" target (→ /v2/plan). */
  planHref: string;
  /** "Draft ahead ›" target (→ /plan, same as the retired InlineAction). */
  draftAheadHref: string;
}

export function PlanLedger({
  weekPlan,
  onToggleDay,
  weeks,
  currentWeek,
  seasonName,
  draftedWeeks,
  meetLabels,
  undraftedUpcomingCount,
  weekHref,
  planHref,
  draftAheadHref,
}: PlanLedgerProps) {
  const [weekOpen, setWeekOpen] = useState<boolean>(readDisclosure);
  // B309 — the chip whose day details are open (collapsed row only; the
  // expanded grid keeps its own selection inside <WeekPlan>).
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const panelId = useId();

  const toggleDisclosure = useCallback(() => {
    setWeekOpen((prev) => {
      const next = !prev;
      writeDisclosure(next);
      return next;
    });
  }, []);

  const selectedDay = selectedId
    ? (weekPlan?.days.find((d) => d.id === selectedId) ?? null)
    : null;

  const total = weeks.length;
  const sublineText = composeSubline(weeks, currentWeek, meetLabels);
  const draftKnown = draftedWeeks != null;

  return (
    <div className={styles.card}>
      {/* ── Row A · THIS WEEK ── (omitted when no weekly plan exists) */}
      {weekPlan && (
        <>
          <div className={styles.header}>
            {/* B354 — the range is one unbreakable run (no-break spaces): beside the
                links the kicker can be a few points short at 375, and a break inside
                "Oct 12 to 18" left the "18" alone on a second line. If it must wrap, it
                wraps before the range, never inside it. The text is otherwise the same. */}
            <span className={styles.kicker}>This week · {weekPlan.dateRange.replace(/ /g, NBSP)}</span>
            <span className={styles.headerRight}>
              <Link to={weekHref} className={styles.openLink}>
                Open week ›
              </Link>
              <button
                type="button"
                className={styles.disclosure}
                onClick={toggleDisclosure}
                aria-expanded={weekOpen}
              >
                {weekOpen ? 'Less' : 'Details'}
                <Chevron up={weekOpen} />
              </button>
            </span>
          </div>

          {weekOpen ? (
            <div className={styles.expanded}>
              <WeekPlan plan={weekPlan} onToggle={onToggleDay} />
            </div>
          ) : (
            <>
              <div className={styles.chips}>
                {weekPlan.days.map((d) => {
                  const clickable = !!onToggleDay && !!d.id;
                  return (
                    <WeekChip
                      key={d.dayShort}
                      day={d}
                      clickable={clickable}
                      selected={clickable && d.id === selectedId}
                      panelId={panelId}
                      onClick={() => setSelectedId((prev) => (prev === d.id ? null : d.id!))}
                    />
                  );
                })}
              </div>
              {selectedDay && (
                <DayDetails
                  key={selectedDay.id}
                  day={selectedDay}
                  onToggle={onToggleDay}
                  id={panelId}
                />
              )}
            </>
          )}

          <div className={styles.divider} />
        </>
      )}

      {/* ── Row B · SEASON ── */}
      <div className={styles.header}>
        {seasonName && <span className={styles.kicker}>Season · {seasonName}</span>}
        <Link to={planHref} className={styles.openLink}>
          Open plan ›
        </Link>
      </div>

      <div className={styles.track}>
        {weeks.map((w, idx) =>
          meetLabels?.[w.week_number] ? (
            <span
              key={`meet-${w.week_number}`}
              className={styles.meetFlag}
              style={{ left: `${((idx + 0.5) / total) * 100}%` }}
              aria-hidden
            >
              ◆
            </span>
          ) : null,
        )}
        <div className={styles.trackRow}>
          {weeks.map((w) => {
            const isCurrent = currentWeek != null && w.week_number === currentWeek;
            const isPast = currentWeek != null && w.week_number < currentWeek;
            const isDrafted = draftKnown ? (draftedWeeks?.has(w.week_number) ?? false) : true;
            const cls = [
              styles.seg,
              SEG_MODE_CLASS[w.mode] ?? styles.segNeutral,
              isDrafted ? styles.segDrafted : styles.segUndrafted,
              isPast ? styles.segPast : '',
              isCurrent ? styles.segCurrent : '',
            ]
              .filter(Boolean)
              .join(' ');
            return <span key={w.week_number} className={cls} aria-hidden />;
          })}
        </div>
      </div>

      <div className={styles.subline}>
        <span className={styles.sublineText}>{sublineText}</span>
        {undraftedUpcomingCount > 0 && (
          <Link to={draftAheadHref} className={styles.draftAhead}>
            Draft ahead ›
          </Link>
        )}
      </div>
    </div>
  );
}

/** A chip token with nothing to say: empty, or only dashes and spaces. */
const PLACEHOLDER_TOKEN = /^[\s–—-]*$/;

const SELECTED_CHIP_STYLE: CSSProperties = {
  outline: '2px solid var(--signal)',
  outlineOffset: -2,
};

function WeekChip({
  day,
  clickable,
  selected,
  panelId,
  onClick,
}: {
  day: Day;
  clickable: boolean;
  selected: boolean;
  panelId: string;
  onClick: () => void;
}) {
  const isRest = day.rest ?? day.mileage === '—';
  const isCompleted = day.completionStatus === 'completed';
  // Collapsed chip shows the leading mileage number only ("3 mi" → "3", "—").
  //
  // ⛔ B28 — do NOT "compact" a duration here. A duration day KEEPS its unit
  // ("45 min") by ruling: that is the B-40 BUG 1 outcome, and the overflow it
  // caused was fixed in CSS instead (.chipMi text-align/nowrap), pinned by
  // PlanLedger.test.tsx. Stripping "min" to "45m" reintroduces the ambiguity
  // that ruling removed.
  //
  // B332 / B314 — the distance is read in the athlete's own unit
  // (`mileageInUnit ?? mileage`, the pair the day panel's title reads), so a km
  // athlete's 10.2 km day shows "10.2", never the "6.3" of the same run in miles.
  const mileageText = day.mileageInUnit ?? day.mileage;
  const mileageShort = mileageText.replace(/\s*(?:mi|km)$/i, '');
  // A screen reader SPEAKS the chip label below, and `day.mileage` is the
  // placeholder "—" for a rest day or a day with no distance
  // (`hooks/useWeeklyPlan`), so the label read "TUE Rest —, tap to mark
  // complete" (critic 1, 2026-09-09; B309 later dropped the action words, the
  // chip opens details now). A token that is only a dash is dropped
  // from the spoken label; the visible cell keeps it, because a mark in a cell
  // is not spoken copy.
  const spokenTokens = [day.dayShort, day.label, mileageText].filter(
    (t) => !PLACEHOLDER_TOKEN.test(t),
  );

  const cls = [
    styles.chip,
    day.today ? styles.chipToday : '',
    !day.today && isCompleted ? styles.chipDone : '',
    !day.today && !isCompleted && isRest ? styles.chipRest : '',
    clickable ? styles.chipClickable : '',
  ]
    .filter(Boolean)
    .join(' ');

  const inner = (
    <>
      <span className={styles.chipDay}>{day.dayShort[0]}</span>
      <span className={`${styles.chipMi} tnum`}>{mileageShort}</span>
      {isCompleted && !day.today && (
        <span className={styles.chipCheck} aria-hidden>
          ✓
        </span>
      )}
    </>
  );

  if (clickable) {
    return (
      <button
        type="button"
        className={cls}
        onClick={onClick}
        // The chip whose details are open: the same outline the week grid's
        // selected cell uses (inline, so the ledger's own stylesheet is untouched).
        style={selected ? SELECTED_CHIP_STYLE : undefined}
        aria-expanded={selected}
        aria-controls={selected ? panelId : undefined}
        // A screen reader SPEAKS this label, and an em dash is not register
        // copy (critic 7, 2026-09-08). A comma is the pause it was standing in
        // for, and VoiceOver reads it as one. Selection is carried by
        // aria-expanded, not by words.
        aria-label={`${spokenTokens.join(' ')}${isCompleted ? ', completed' : ''}`}
      >
        {inner}
      </button>
    );
  }
  return <div className={cls}>{inner}</div>;
}

/**
 * A2 — `sys.caret` @11. The ratified art is a DOWN caret; the UP state rotates
 * it in a wrapper rather than swapping to a mirrored path, so the two
 * directions are provably the same mark. R7 optical: the legacy pair is a
 * 14-grid at stroke 2, the ratified one a 24-grid at 1.5.
 */
function Chevron({ up }: { up: boolean }) {
  const iconsOn = useIconSystem();
  if (iconsOn) {
    return (
      <span
        aria-hidden
        style={{ display: 'inline-flex', transform: up ? 'rotate(180deg)' : undefined }}
      >
        <Icon name="sys.caret" size={11} />
      </span>
    );
  }
  return (
    <svg width="11" height="11" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      {up ? <path d="M3 9l4-4 4 4" /> : <path d="M3 5l4 4 4-4" />}
    </svg>
  );
}

/**
 * "W2 of 7 · Base · State Meet in 3 wks" — the season sub-line. Bold W-count,
 * the current phase label, and a countdown to the CULMINATING (last upcoming)
 * meet — the frames anchor on the season's championship meet, not the nearest
 * one. The meet clause is omitted when no upcoming meet exists.
 */
function composeSubline(
  weeks: MacroPlanWeek[],
  currentWeek: number | null,
  meetLabels?: Record<number, string>,
): ReactNode {
  const total = weeks.length;
  const tail: string[] = [];

  const currentMode = weeks.find((w) => w.week_number === currentWeek)?.mode;
  const phaseLabel = currentMode ? PHASE_LABEL[currentMode] : undefined;
  if (phaseLabel) tail.push(phaseLabel);

  if (meetLabels && currentWeek != null) {
    const upcoming = Object.keys(meetLabels)
      .map(Number)
      .filter((n) => n >= currentWeek)
      .sort((a, b) => a - b);
    const meetWeek = upcoming.length > 0 ? upcoming[upcoming.length - 1] : null;
    if (meetWeek != null) {
      const away = meetWeek - currentWeek;
      const when = away <= 0 ? 'this week' : `in ${away} wk${away === 1 ? '' : 's'}`;
      tail.push(`${meetLabels[meetWeek]} ${when}`);
    }
  }

  return (
    <>
      <b>{currentWeek != null ? `W${currentWeek} of ${total}` : `${total} weeks`}</b>
      {tail.length > 0 ? ` · ${tail.join(' · ')}` : ''}
    </>
  );
}

export default PlanLedger;
